// ---------------------------------------------------------------------------
// Leadership and company accountability content.
//
// WHY THIS FILE EXISTS
// Enterprise buyers do not sign with a data company that has no name behind it.
// Third party risk teams look for a real person accountable for the security
// program, and prospects look for a founder they can search for. This file is
// where those names live.
//
// HOW TO PUBLISH A LEADER
//  1. Fill in name, role, bio, focus and linkedIn on the entry below.
//  2. Add a square headshot to /public/team/ and set `photo` to that path. If
//     there is no photo yet, leave `photo` empty and the initials avatar is used.
//  3. Set `published: true`.
// The Leadership section on the About page only renders once at least one entry
// is published, so nothing placeholder ever reaches a visitor.
// ---------------------------------------------------------------------------

export interface TeamMember {
  name: string;
  role: string;
  /** Two or three sentences. Written in third person, no marketing adjectives. */
  bio: string;
  /** Short label for what this person owns day to day. */
  focus: string;
  /** Full LinkedIn profile URL. Leave empty to hide the link. */
  linkedIn: string;
  /** Path to a square headshot under /public. Leave empty to use initials. */
  photo: string;
  /** Set to true only when the name, role and bio are approved for publication. */
  published: boolean;
}

export const LEADERSHIP: TeamMember[] = [
  {
    name: "",
    role: "Founder and Chief Executive Officer",
    bio: "",
    focus: "Company direction, data partnerships and customer outcomes",
    linkedIn: "",
    photo: "",
    published: false
  },
  {
    name: "",
    role: "Qualified Individual, Information Security",
    bio: "",
    focus: "Security program, FTC Safeguards Rule compliance and vendor review",
    linkedIn: "",
    photo: "",
    published: false
  },
  {
    name: "",
    role: "Head of Data",
    bio: "",
    focus: "Death record sourcing, match quality and data freshness",
    linkedIn: "",
    photo: "",
    published: false
  }
];

/** Only fully filled in, approved entries are safe to render publicly. */
export const PUBLISHED_LEADERSHIP: TeamMember[] = LEADERSHIP.filter(
  member => member.published && member.name.trim().length > 0
);

// ---------------------------------------------------------------------------
// Founder message shown on the About page.
//
// TO FINISH THIS SECTION
//  1. Set `name` to the founder's real name. Until it is filled in, the
//     signature falls back to the role alone so no invented name ever ships.
//  2. Replace /public/team/founder.svg with a real portrait (portrait crop,
//     roughly 3:4, at least 900px tall) and point `photo` at it.
//  3. Add the LinkedIn URL so buyers can verify the person exists.
// ---------------------------------------------------------------------------
export const FOUNDER = {
  name: "",
  role: "Founder and Chief Executive Officer",
  photo: "/team/Morry.webp",
  photoAlt: "Founder of VerifyDeceased",
  linkedIn: "",
  since: "2012",
  paragraphs: [
    "I started in this business looking for houses, not fraud. For more than a decade our team read obituaries every single day to find estates that were about to change hands, and somewhere past the twenty millionth record it stopped being a real estate problem.",
    "We kept running into the same thing. A person passes away, and for weeks or months afterwards there is no practical way for a bank, an employer or an insurer to find that out. In that silence, someone opens a credit card in their name, or a family gets a collections call about a person they just buried.",
    "We already had the research operation to close that gap, so we built VerifyDeceased on top of it. If you are evaluating us, call the number on this site. You will get a person, and you will get straight answers about where the data comes from and what it can and cannot do."
  ]
};

export function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join("");
}

export interface CompanyFact {
  label: string;
  value: string;
  detail: string;
}

/**
 * The verifiable, checkable facts about who a customer is contracting with.
 * Everything here must be true and independently confirmable. This panel is the
 * one an enterprise procurement team screenshots.
 */
export const COMPANY_FACTS: CompanyFact[] = [
  {
    label: "Operating entity",
    value: "VerifyDeceased by LeadCruncher",
    detail: "A product of LeadCruncher, an established United States data platform."
  },
  {
    label: "Headquarters",
    value: "Alta Loma, California",
    detail: "A real, staffed United States address, published on our contact page."
  },
  {
    label: "Data residency",
    value: "United States only",
    detail: "Hosting, processing and backups all stay within US regions."
  },
  {
    label: "Security accountability",
    value: "Named Qualified Individual",
    detail: "A single named owner of the information security program, as the Safeguards Rule requires."
  },
  {
    label: "Reachable by phone",
    value: "Direct line, not a form",
    detail: "Sales and support both answer a published phone number during business hours."
  },
  {
    label: "Contract commitment",
    value: "None required to start",
    detail: "No minimum, no subscription and no cancellation fee on standard pricing."
  }
];
